import { useState } from 'react';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Spinner } from 'components';
import { CheckIcon } from 'assets';
import { CreateRoomForm, SubscriptionStep } from './components';
import { IApiRequests, useApiRequests, useUser } from 'hooks';
import { IUserContext } from 'context';
import { useAppDispatch } from 'store';
import { getUser } from 'store/user';
import { UpdateClinicUserRequest, UpdateUserRequest } from 'types';
import { SECOND_ACCOUNT_PAYMENT_REQUIRED } from 'constants/index';
import { HIPAA_BENEFITS } from 'constants/texts';
import { isHipaaLink } from 'config/productProfile';

import styles from './RegisterSecondAccountPage.module.scss';
import image from '../../assets/images/LoginLeftImage.png';

export function RegisterSecondAccountPage() {
  const { t } = useTranslation('auth');
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const { enqueueSnackbar } = useSnackbar();

  const { updateClinicUser, updateProfilePicture, updateUser, skipCreateRoom, ensureRoomNameIsUnique }: IApiRequests =
    useApiRequests();

  const { registerSecondAccount }: IUserContext = useUser();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<number>(() =>
    localStorage.getItem(SECOND_ACCOUNT_PAYMENT_REQUIRED) === 'true' ? 3 : 2,
  );

  const handleSubmit = async (
    profilePicture: File | null,
    profilePictureChanged: boolean,
    user: UpdateUserRequest,
    clinicUser: UpdateClinicUserRequest,
    organization: string,
  ): Promise<void> => {
    setLoading(true);

    const ensureRoomNameIsUniquePromise = (): Promise<void> => {
      return ensureRoomNameIsUnique(clinicUser.roomName ?? '').catch((error) => {
        return Promise.reject(t('registrationError', { message: error.message }));
      });
    };

    const registerPromise = (): Promise<void> => {
      return registerSecondAccount(organization).catch((e) => Promise.reject(t('registrationError', { message: e })));
    };

    const updateImagePromise = (): Promise<void> => {
      if (!profilePictureChanged) {
        return Promise.resolve();
      }

      return updateProfilePicture(profilePicture).catch((error) => {
        return Promise.reject(t('unableToUpdateProfilePicture', { message: error.message }));
      });
    };

    const updateClinicUserPromise = (): Promise<any> => {
      return updateClinicUser(clinicUser).catch((error: any) => {
        return Promise.reject(t('updateClinicUserFailed', { message: error.message }));
      });
    };

    const skipCreateRoomPromise = (): Promise<any> => {
      return skipCreateRoom().catch((error: any) => {
        return Promise.reject(t('updateClinicUserFailed', { message: error.message }));
      });
    };

    const updateUserPromise = (): Promise<any> => {
      return updateUser(user).catch((error: any) => {
        return Promise.reject(t('updateUserFailed', { message: error.message }));
      });
    };

    return ensureRoomNameIsUniquePromise()
      .then(() => registerPromise())
      .then(() => updateImagePromise())
      .then(() => updateClinicUserPromise())
      .then(() => skipCreateRoomPromise())
      .then(() => updateUserPromise())
      .then(() => dispatch(getUser()).unwrap())
      .then(() => {
        localStorage.setItem(SECOND_ACCOUNT_PAYMENT_REQUIRED, 'true');
        setStep(3);
      })
      .catch((error) => {
        enqueueSnackbar(error, { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box className={styles.registerContainer}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={classnames(styles.headerItem, styles.headerItem1)}>
            <Typography variant='body1'>1</Typography>
            <Typography variant='body4'>{t('registerHeaderProfile')}</Typography>
          </div>
          <div
            className={classnames(styles.headerItem, styles.headerItem2, { [styles.headerItem2Complete]: step === 3 })}
          >
            <Typography variant='body1'>2</Typography>
            <Typography variant='body4'>{t('registerHeaderCreateRoom')}</Typography>
          </div>
          <div className={classnames(styles.headerItem, { [styles.headerItem3Active]: step === 3 })}>
            <Typography variant='body1'>3</Typography>
            <Typography variant='body4'>{t('registerHeaderGetStarted')}</Typography>
          </div>
        </div>

        <div className={styles.body}>
          {loading && <Spinner small />}
          <Box className={styles.leftSide}>
            <img src={image} />

            {step === 2 && (
              <>
                <Typography variant={isDesktop ? 'h1' : 'h4'} component={isDesktop ? 'h1' : 'h4'}>
                  {t('secondAccountCreateAdditionalBusinessProfile')}
                </Typography>

                <Typography variant='body2' component='span'>
                  {t('secondAccountRoomForNewAdmin')}
                </Typography>

                <Typography variant='body2' component='span'>
                  {t('secondAccountSwitchOrganizations')}&nbsp;
                </Typography>
              </>
            )}

            {step === 3 && (
              <>
                <Typography variant={isDesktop ? 'h1' : 'h4'} component={isDesktop ? 'h1' : 'h4'}>
                  <>
                    {t('benefitsOfHipaa')}
                    <br />
                    {isHipaaLink ? (
                      <>
                        HIPAA<span className={styles.link}>LINK</span>
                      </>
                    ) : (
                      <>
                        <strong>VIA3</strong>
                      </>
                    )}
                  </>
                </Typography>

                <ul className={styles.benefitsList}>
                  {HIPAA_BENEFITS.map((benefit: string, index: number) => (
                    <li key={index}>
                      <CheckIcon />
                      <Typography variant='body2' component='span'>
                        {t(benefit)}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Box>
          <Box className={styles.rigthSide}>
            {step === 2 && <CreateRoomForm onSubmit={handleSubmit} />}
            {step === 3 && <SubscriptionStep />}
          </Box>
        </div>
      </div>
    </Box>
  );
}
